'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';

/**
 * Copy-on-first-use seeding: COPIES each chosen admin template into the caller's
 * family `characters` library as a normal, editable character (new id,
 * creationMethod='template'). Idempotent per (familyId, template): the copy id is
 * derived deterministically, so re-picking the same template never duplicates —
 * the existing character id is returned.
 *
 * The signature carries `{ familyId, creatorId }` for API clarity, but the
 * SESSION is the source of truth (a client can never seed into another family).
 *
 * @returns the resulting character ids, in the same order as `templateIds`.
 */
export async function seedCharactersFromTemplates(
  templateIds: string[],
  ctx?: { familyId?: string; creatorId?: string },
): Promise<ActionResult<{ characterIds: string[] }>> {
  try {
    const familyId = (await SessionService.getFamilyId()) ?? ctx?.familyId;
    const creatorId = (await SessionService.getUserId()) ?? ctx?.creatorId;
    if (!familyId || !creatorId) return { success: false, error: 'Not authenticated' };

    if (!templateIds.length) return { success: true, data: { characterIds: [] } };

    const { getCharacterRepository, getCharacterTemplateRepository } = await import(
      '@/contexts/characters/repositories'
    );
    const characterRepo = getCharacterRepository();
    const templateRepo = getCharacterTemplateRepository();

    const characterIds: string[] = [];

    for (const templateId of templateIds) {
      // Deterministic id per (family, template) → idempotent re-picks.
      const seededId = `seed_${familyId}_${templateId}`;

      const existing = await characterRepo.getById(seededId);
      if (existing) {
        characterIds.push(existing.id);
        continue;
      }

      const template = await templateRepo.getTemplate(templateId);
      if (!template || !template.isActive) continue;

      const { getLocale } = await import('next-intl/server');
      const { localizedTemplateName } = await import('../utils/systemCharacter');
      const locale = await getLocale().catch(() => 'en');

      const created = await characterRepo.create({
        id: seededId,
        familyId,
        creatorId,
        // Copied in the parent's language — the row is theirs to rename afterwards.
        name: localizedTemplateName(template, locale),
        role: template.role ?? undefined,
        typicalRole: template.typicalRole ?? undefined,
        gender: template.gender ?? undefined,
        age: template.age ?? undefined,
        creationMethod: 'template',
        physicalDescription: template.physicalDescription ?? undefined,
        personalityTraits: template.personalityTraits ?? undefined,
        specialAbilities: template.specialAbilities ?? undefined,
        visualDescription: template.visualDescription ?? undefined,
        synonyms: template.synonyms ?? [],
        primaryImageUrl: template.primaryImageUrl ?? undefined,
        primaryPortraitUrl: template.primaryPortraitUrl ?? undefined,
      });

      characterIds.push(created.id);
    }

    return { success: true, data: { characterIds } };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('seedCharactersFromTemplates', e) };
  }
}
